import React, { CSSProperties, useState } from 'react';
import { TextField, Tooltip } from '@mui/material';

import AssetService from '../../shared/services/AssetService';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Unstable_Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import styles from '../styles/CourseCatalogCard.module.css';
import useScreenSize from '../../content/hooks/useScreenSize';

interface CourseCardProps {
  /** Name of group */
  title: string;
  /** Optional description. */
  description?: string;
  /** Optional asset key to an avatar image for a group. */
  assetkey?: string;
  /** Optional cost to joint the group, default 'FREE'. */
  priceTag?: string;
  /** Group code */
  code: string;
  /** Group join by method */
  groupJoinBy: string;
  /** Optional value indicating that the user has already joined. */
  joined?: boolean;
  /** Callback when the user clicks to join the group. */
  onJoin: () => void;
  /** Callback when the user clicks to join the group by `code`. */
  onJoinByCode: (code: string) => void;
}

const CourseCard = (props: CourseCardProps) => {
  const { title, assetkey, code, priceTag, groupJoinBy } = props;
  const [joining, setJoining] = useState(false);
  const [userCode, setUserCode] = useState<string>('');
  const [invalidCode, setInvalidCode] = useState<boolean>(undefined);
  const url = AssetService.getBlobUrl(assetkey);
  const screenWidth = useScreenSize();
  const isMobileView = screenWidth < 700;

  const handleJoin = () => {
    setJoining(true);
    props.onJoin();
  };

  const handleJoinByCode = async () => {
    if (code?.toLocaleLowerCase() === userCode?.toLocaleLowerCase()) {
      setInvalidCode(false);
      setJoining(true);
      props.onJoinByCode(userCode);
    } else {
      setInvalidCode(true);
    }
  };

  const buttonProps: CSSProperties = {
    width: 'auto',
    padding: 4,
    paddingLeft: isMobileView ? 52 : ''
  };

  const helperTextProps: CSSProperties = {
    position: 'relative',
    top: -6,
    lineHeight: 'unset',
    marginTop: 'unset'
  };

  const codeContainer: CSSProperties = isMobileView
    ? { paddingBottom: 4, marginTop: -8 }
    : {};

  return (
    <Grid container direction="column">
      <Grid container justifyContent="space-between">
        <Grid>
          <Typography variant="button" className={styles.courseCardHeader}>
            AVAILABLE CONTENT
          </Typography>
        </Grid>
        <Grid>
          <Typography variant="button" className={styles.courseCardHeader}>
            {priceTag}
          </Typography>
        </Grid>
      </Grid>
      <Paper className={styles.courseCardContainer}>
        <Grid
          container
          spacing={1}
          alignItems="center"
          style={{ minHeight: 60 }}
        >
          <Grid>
            {url ? (
              <Avatar src={url}></Avatar>
            ) : (
              <Avatar>{title[0].toLocaleUpperCase()}</Avatar>
            )}
          </Grid>
          <Grid className={styles.courseCardInfo}>
            <Grid container direction="column">
              <Grid xs={12} className="title-label">
                <Tooltip title={props.title} placement="bottom-start">
                  <Typography variant="subtitle1">{props.title}</Typography>
                </Tooltip>
              </Grid>
              <Grid xs={12} className="title-label">
                <Tooltip title={props.description} placement="bottom-start">
                  <Typography variant="subtitle2">
                    {props.description}
                  </Typography>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
          <Grid style={buttonProps}>
            {props.joined ? (
              <Grid>
                <Button
                  variant="text"
                  size="small"
                  color="primary"
                  className={styles.enrolledButton}
                >
                  Enrolled
                </Button>
              </Grid>
            ) : code ? (
              <Grid
                container
                alignItems="center"
                spacing={1}
                style={codeContainer}
              >
                <Grid className={styles.voucherTextField}>
                  <TextField
                    label="Access Code"
                    type="text"
                    size="small"
                    variant="outlined"
                    value={userCode}
                    error={invalidCode}
                    helperText={invalidCode ? 'Invalid code' : ' '}
                    onChange={(e) => setUserCode(e.target.value)}
                    InputLabelProps={{
                      sx: {
                        '&.MuiInputLabel-shrink': {
                          transform: `translate(14px, -5px) scale(0.75)`
                        },
                        fontSize: 15,
                        top: -12
                      }
                    }}
                    InputProps={{
                      style: { top: -8 }
                    }}
                    FormHelperTextProps={{
                      style: helperTextProps
                    }}
                    style={{
                      transform: 'translateY(-10px)'
                    }}
                    inputProps={{
                      style: {
                        padding: 4
                      },
                      shrink: true
                    }}
                  />
                </Grid>
                <Grid>
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    disabled={joining || !userCode}
                    onClick={handleJoinByCode}
                  >
                    {joining ? 'Enrolling...' : 'Enroll'}
                  </Button>
                </Grid>
              </Grid>
            ) : (
              <Grid>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  disabled={joining}
                  onClick={handleJoin}
                >
                  {joining ? 'Enrolling...' : groupJoinBy}
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default CourseCard;
